import { z } from 'zod';

// --- Boolean-returning utility functions for validation logic ---

/**
 * Checks if a string is not empty after trimming.
 */
export const isNotEmpty = (value: string | null | undefined): boolean => {
  return !!value && value.trim().length > 0;
};

/**
 * Validates an email format.
 */
export const isEmailValid = (email: string): boolean => {
  if (!isNotEmpty(email)) return false; // Allow Zod's .min(1) to handle empty message if separate
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

/**
 * Validates a US-like phone number format.
 * Allows optional country code, parentheses, spaces, hyphens, dots.
 */
export const isPhoneValid = (phone: string): boolean => {
  if (!isNotEmpty(phone)) return false; // Allow Zod's .min(1) to handle empty message
  const phoneRegex = /^(\+\d{1,3})?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  return phoneRegex.test(phone.trim());
};

/**
 * Validates a generic name (e.g., first name, last name, business name part).
 * Checks for minimum length.
 */
export const isNameValid = (name: string, minLength: number = 2): boolean => {
  if (!isNotEmpty(name)) return false;
  return name.trim().length >= minLength;
};

// --- Reusable Zod schemas for common fields ---

export const requiredStringSchema = (fieldName: string) => 
  z.string({ required_error: `${fieldName} is required.` })
   .trim()
   .min(1, { message: `${fieldName} is required.` });

export const emailFieldSchema = 
  requiredStringSchema("Email")
  .email({ message: "Invalid email address format." });
  // Note: Zod's .email() is generally robust. 
  // If more specific regex control is needed than Zod's default, 
  // .refine(isEmailValid, "Invalid email address format (custom check)") could be used instead of .email().
  // For now, relying on Zod's .email() after .trim().min(1).

export const phoneFieldSchema = 
  requiredStringSchema("Phone number")
  .refine(isPhoneValid, { message: "Invalid phone number format." });

export const genericNameFieldSchema = (fieldName: string, minLength: number = 2, maxLength?: number) => {
  let schema = requiredStringSchema(fieldName)
    .min(minLength, { message: `${fieldName} must be at least ${minLength} characters.` });
  if (maxLength) {
    schema = schema.max(maxLength, { message: `${fieldName} must not exceed ${maxLength} characters.` });
  }
  return schema;
};

export const businessNameSchema = genericNameFieldSchema("Business name", 2, 100);
export const shortBusinessNameSchema = genericNameFieldSchema("Short business name", 2, 30);

// Example for optional fields that still need validation if provided
export const optionalEmailFieldSchema = z.string().trim()
  .email({ message: "Invalid email address format." })
  .optional()
  .or(z.literal('')); // Allow empty string to mean "not provided"

export const optionalWebsiteFieldSchema = z.string().trim()
  .url({ message: "Invalid website URL." })
  .optional()
  .or(z.literal(''));

export const optionalPhoneFieldSchema = z.string().trim()
  .refine(value => value === '' || isPhoneValid(value), { message: "Invalid phone number format." })
  .optional()
  .or(z.literal(''));

export const optionalGenericNameFieldSchema = (fieldName: string, minLength: number = 2, maxLength?: number) => {
  let schema = z.string().trim()
    .refine(value => {
      if (value === '') return true; // Allow empty string
      if (value.length < minLength) return false;
      if (maxLength && value.length > maxLength) return false;
      return true;
    }, value => {
      if (value === '') return { message: `${fieldName} is optional.` }; // Should not happen if schema is optional
      if (value.length < minLength) return { message: `${fieldName} must be at least ${minLength} characters.` };
      if (maxLength && value.length > maxLength) return { message: `${fieldName} must not exceed ${maxLength} characters.` };
      return { message: "Invalid input" }; // Generic fallback
    })
    .optional()
    .or(z.literal(''));
  return schema;
};
